import { isExcludedCountryDisplayName } from '@/modules/countries/utils/excludedDisplayCountries'

/**
 * Static regional groupings for Popular Destinations (Home + Shop Plans): showcase order and labels.
 * Live country counts use the merged local bundles API (see Shop and Home regional tab after load).
 */

export const REGION_GROUP_ORDER = [
  'europe',
  'asia',
  'americas',
  'middleEast',
  'africa',
  'oceania',
] as const

export type RegionGroupKey = (typeof REGION_GROUP_ORDER)[number]

export const REGION_GROUP_LABELS: Record<RegionGroupKey, string> = {
  europe: 'Europe',
  asia: 'Asia',
  americas: 'Americas',
  middleEast: 'Middle East',
  africa: 'Africa',
  oceania: 'Oceania',
}

export const REGION_COUNTRY_LISTS: Record<RegionGroupKey, Array<{ name: string; flag: string }>> = {
  europe: [
    { name: 'United Kingdom', flag: '🇬🇧' },
    { name: 'Germany', flag: '🇩🇪' },
    { name: 'France', flag: '🇫🇷' },
    { name: 'Italy', flag: '🇮🇹' },
    { name: 'Spain', flag: '🇪🇸' },
    { name: 'Netherlands', flag: '🇳🇱' },
    { name: 'Belgium', flag: '🇧🇪' },
    { name: 'Switzerland', flag: '🇨🇭' },
    { name: 'Austria', flag: '🇦🇹' },
    { name: 'Sweden', flag: '🇸🇪' },
    { name: 'Norway', flag: '🇳🇴' },
    { name: 'Denmark', flag: '🇩🇰' },
    { name: 'Finland', flag: '🇫🇮' },
    { name: 'Poland', flag: '🇵🇱' },
    { name: 'Portugal', flag: '🇵🇹' },
    { name: 'Greece', flag: '🇬🇷' },
    { name: 'Ireland', flag: '🇮🇪' },
    { name: 'Czech Republic', flag: '🇨🇿' },
    { name: 'Hungary', flag: '🇭🇺' },
    { name: 'Romania', flag: '🇷🇴' },
    { name: 'Bulgaria', flag: '🇧🇬' },
    { name: 'Croatia', flag: '🇭🇷' },
    { name: 'Slovenia', flag: '🇸🇮' },
    { name: 'Slovakia', flag: '🇸🇰' },
    { name: 'Estonia', flag: '🇪🇪' },
    { name: 'Latvia', flag: '🇱🇻' },
    { name: 'Lithuania', flag: '🇱🇹' },
    { name: 'Iceland', flag: '🇮🇸' },
    { name: 'Luxembourg', flag: '🇱🇺' },
    { name: 'Malta', flag: '🇲🇹' },
    { name: 'Cyprus', flag: '🇨🇾' },
  ],
  asia: [
    { name: 'Japan', flag: '🇯🇵' },
    { name: 'South Korea', flag: '🇰🇷' },
    { name: 'China', flag: '🇨🇳' },
    { name: 'India', flag: '🇮🇳' },
    { name: 'Singapore', flag: '🇸🇬' },
    { name: 'Malaysia', flag: '🇲🇾' },
    { name: 'Thailand', flag: '🇹🇭' },
    { name: 'Indonesia', flag: '🇮🇩' },
    { name: 'Philippines', flag: '🇵🇭' },
    { name: 'Vietnam', flag: '🇻🇳' },
    { name: 'Taiwan', flag: '🇹🇼' },
    { name: 'Hong Kong', flag: '🇭🇰' },
    { name: 'Bangladesh', flag: '🇧🇩' },
    { name: 'Pakistan', flag: '🇵🇰' },
    { name: 'Sri Lanka', flag: '🇱🇰' },
    { name: 'Nepal', flag: '🇳🇵' },
    { name: 'Bhutan', flag: '🇧🇹' },
    { name: 'Myanmar', flag: '🇲🇲' },
    { name: 'Cambodia', flag: '🇰🇭' },
    { name: 'Laos', flag: '🇱🇦' },
    { name: 'Brunei', flag: '🇧🇳' },
    { name: 'Maldives', flag: '🇲🇻' },
    { name: 'Mongolia', flag: '🇲🇳' },
    { name: 'Kazakhstan', flag: '🇰🇿' },
    { name: 'Uzbekistan', flag: '🇺🇿' },
    { name: 'Kyrgyzstan', flag: '🇰🇬' },
    { name: 'Tajikistan', flag: '🇹🇯' },
    { name: 'Turkmenistan', flag: '🇹🇲' },
    { name: 'Afghanistan', flag: '🇦🇫' },
  ],
  americas: [
    { name: 'United States', flag: '🇺🇸' },
    { name: 'Canada', flag: '🇨🇦' },
    { name: 'Mexico', flag: '🇲🇽' },
    { name: 'Brazil', flag: '🇧🇷' },
    { name: 'Argentina', flag: '🇦🇷' },
    { name: 'Chile', flag: '🇨🇱' },
    { name: 'Peru', flag: '🇵🇪' },
    { name: 'Colombia', flag: '🇨🇴' },
    { name: 'Venezuela', flag: '🇻🇪' },
    { name: 'Ecuador', flag: '🇪🇨' },
    { name: 'Bolivia', flag: '🇧🇴' },
    { name: 'Paraguay', flag: '🇵🇾' },
    { name: 'Uruguay', flag: '🇺🇾' },
    { name: 'Costa Rica', flag: '🇨🇷' },
    { name: 'Panama', flag: '🇵🇦' },
    { name: 'Nicaragua', flag: '🇳🇮' },
    { name: 'Honduras', flag: '🇭🇳' },
    { name: 'Guatemala', flag: '🇬🇹' },
    { name: 'Belize', flag: '🇧🇿' },
    { name: 'El Salvador', flag: '🇸🇻' },
    { name: 'Jamaica', flag: '🇯🇲' },
    { name: 'Trinidad and Tobago', flag: '🇹🇹' },
    { name: 'Barbados', flag: '🇧🇧' },
    { name: 'Bahamas', flag: '🇧🇸' },
    { name: 'Dominican Republic', flag: '🇩🇴' },
    { name: 'Haiti', flag: '🇭🇹' },
    { name: 'Cuba', flag: '🇨🇺' },
    { name: 'Puerto Rico', flag: '🇵🇷' },
  ],
  middleEast: [
    { name: 'UAE', flag: '🇦🇪' },
    { name: 'Saudi Arabia', flag: '🇸🇦' },
    { name: 'Israel', flag: '🇮🇱' },
    { name: 'Jordan', flag: '🇯🇴' },
    { name: 'Lebanon', flag: '🇱🇧' },
    { name: 'Qatar', flag: '🇶🇦' },
    { name: 'Kuwait', flag: '🇰🇼' },
    { name: 'Bahrain', flag: '🇧🇭' },
    { name: 'Oman', flag: '🇴🇲' },
    { name: 'Turkey', flag: '🇹🇷' },
    { name: 'Iraq', flag: '🇮🇶' },
    { name: 'Iran', flag: '🇮🇷' },
    { name: 'Yemen', flag: '🇾🇪' },
    { name: 'Syria', flag: '🇸🇾' },
    { name: 'Palestine', flag: '🇵🇸' },
  ],
  africa: [
    { name: 'South Africa', flag: '🇿🇦' },
    { name: 'Egypt', flag: '🇪🇬' },
    { name: 'Morocco', flag: '🇲🇦' },
    { name: 'Kenya', flag: '🇰🇪' },
    { name: 'Nigeria', flag: '🇳🇬' },
    { name: 'Algeria', flag: '🇩🇿' },
    { name: 'Tunisia', flag: '🇹🇳' },
    { name: 'Libya', flag: '🇱🇾' },
    { name: 'Sudan', flag: '🇸🇩' },
    { name: 'Ethiopia', flag: '🇪🇹' },
    { name: 'Tanzania', flag: '🇹🇿' },
    { name: 'Uganda', flag: '🇺🇬' },
    { name: 'Ghana', flag: '🇬🇭' },
    { name: 'Senegal', flag: '🇸🇳' },
    { name: 'Ivory Coast', flag: '🇨🇮' },
    { name: 'Cameroon', flag: '🇨🇲' },
    { name: 'Angola', flag: '🇦🇴' },
    { name: 'Mozambique', flag: '🇲🇿' },
    { name: 'Madagascar', flag: '🇲🇬' },
    { name: 'Zambia', flag: '🇿🇲' },
    { name: 'Zimbabwe', flag: '🇿🇼' },
    { name: 'Botswana', flag: '🇧🇼' },
    { name: 'Namibia', flag: '🇳🇦' },
    { name: 'Mauritius', flag: '🇲🇺' },
    { name: 'Seychelles', flag: '🇸🇨' },
    { name: 'Rwanda', flag: '🇷🇼' },
    { name: 'Malawi', flag: '🇲🇼' },
  ],
  oceania: [
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'New Zealand', flag: '🇳🇿' },
    { name: 'Papua New Guinea', flag: '🇵🇬' },
    { name: 'Fiji', flag: '🇫🇯' },
    { name: 'Samoa', flag: '🇼🇸' },
    { name: 'Tonga', flag: '🇹🇴' },
    { name: 'Vanuatu', flag: '🇻🇺' },
    { name: 'Solomon Islands', flag: '🇸🇧' },
  ],
}

/** Regional showcase entries with UI-only exclusions applied (API unchanged). */
export function getRegionalCountriesForDisplay(key: RegionGroupKey) {
  return REGION_COUNTRY_LISTS[key].filter((c) => !isExcludedCountryDisplayName(c.name))
}
